/**
 * The table view's store (spec M1-B4 §6 "Store").
 *
 * One zustand store per mounted board view, created by `TableViewProvider` and handed the
 * `BoardDataSource`. It holds the loaded payload, the effective view settings, `dirty` (which
 * drives every "Save to this view" button), the cell selection, the row selection, the editor,
 * the undo stack and the open pane/menu — and nothing about how any of it is rendered.
 *
 * Every mutation goes through the data source, so swapping the in-memory source for the
 * tRPC one in round 3 changes nothing here.
 */
import type {
  BoardId,
  ColumnAggregation,
  ColumnId,
  GroupId,
  ItemId,
  PresenceRoster,
  ViewId,
} from '@elizmonday/shared';
import type { StorePatch } from '../../../lib/realtime/patches';
import { OptimisticTracker, newMutationId } from '../../../lib/realtime/optimistic';
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { findColumnType } from '@elizmonday/shared';
import { getColumnUi } from '../columns/registry';
import type { CellRenderContext } from '../columns/types';
import type {
  BoardColumn,
  BoardDataSource,
  BoardViewPayload,
  CellWrite,
  CellWriteResult,
  FacetValue,
  GroupPlacement,
  ItemPlacement,
  ItemRow,
  QueryState,
} from '../data/types';
import { isPostWriteError } from '../data/errors';
// P-129: the one place that reads a tRPC error code without importing server code.
import { errorCode } from '../../../app/errors';
import { chunk, clipBlock, parseTsv, PASTE_CHUNK_SIZE, pasteSummary, toHtmlTable, toTsv } from './clipboard';
import {
  EMPTY_ITEM_SELECTION,
  EMPTY_SELECTION,
  focusCell,
  rangeAddresses,
  type CellAddress,
  type GridGeometry,
  type GridSelection,
  type ItemSelection,
} from './selection';
import {
  DEFAULT_TABLE_VIEW_SETTINGS,
  isDirty,
  pruneStaleColumnRefs,
  visibleColumns,
  type TableViewSettings,
} from './settings';
import {
  archiveLabel,
  cellKey,
  clearLabel,
  deleteLabel,
  dropUndo,
  EMPTY_UNDO_STACK,
  findUndo,
  invalidate,
  massEditLabel,
  peekRedo,
  peekUndo,
  popRedo,
  pushRedo,
  pushUndo,
  restoreRedo,
  type UndoEntry,
  type UndoStack,
} from './undo';

export type PaneKind =
  | 'quick-filter'
  | 'advanced-filter'
  | 'sort'
  | 'hide'
  | 'group-by'
  | 'person'
  | 'search'
  | 'more'
  | 'add-column'
  | null;

export interface MenuState {
  readonly kind: 'column' | 'group' | 'item' | 'column-summary' | 'board';
  readonly targetId: string;
  /**
   * **P-304.** Set when the row whose menu this is is a **subitem**, and it is that subitem's
   * parent. The row menu is monday's own, captured on the sandbox
   * (`discovery/ui-reference/subitems/05-subitem-row-menu.json`): a subitem's menu is not the
   * item menu — it says *Open subitem*, *Copy subitem link*, *Move to Item*, *Convert to
   * item*, and its Archive and Delete act on the **subitems** board.
   */
  readonly parentItemId?: string;
  readonly x: number;
  readonly y: number;
  /**
   * B10: opened by a right-click rather than by the row's `···`. monday's context menu is the
   * hover menu **plus** `Copy name` — one row taller, 220 × 452 against 220 × 420
   * (`[ui:library/item-02-context-menu.json]`, `[c:item-menu]`).
   */
  readonly context?: boolean;
}

/**
 * **P-304** — the board a row action writes to, and the parent it repaints. Only a subitem
 * needs one: `store.removeItems` called `items.remove` with the **parent board's** id, and a
 * subitem lives on the subitems board, so the service matched no rows and the Delete on a
 * subitem did nothing at all — no dialog, no toast, the row still there (Nico: "I cannot
 * cancel subitems").
 */
export interface RowTarget {
  readonly boardId: BoardId;
  readonly parentItemId: ItemId;
}

export interface ToastState {
  readonly id: number;
  readonly message: string;
  readonly actionLabel?: string;
  readonly kind: 'undo' | 'info' | 'error';
  /**
   * The undo entry this toast's **Undo** belongs to (P-050). Without it the button undid
   * "the last thing on the stack", which after a delete could be a cell edit made while the
   * toast was still up — the reported defect. `Ctrl/Cmd+Z` keeps taking the top of the stack.
   */
  readonly undoEntryId?: string;
}

export const NAME_COLUMN_ID = 'name' as ColumnId;

/**
 * Undo entry ids. A counter, not just the clock: the toast's **Undo** looks its entry up by
 * id, and two batches inside the same millisecond must not share one (P-050).
 */
let undoSequence = 0;
function nextUndoId(prefix: string): string {
  undoSequence += 1;
  return `${prefix}-${Date.now()}-${undoSequence}`;
}

export interface TableState {
  readonly boardId: string;
  readonly viewId: ViewId;
  status: 'loading' | 'ready' | 'error';
  error: string | null;
  payload: BoardViewPayload | null;
  /** Rows appended by `boardView.page`, per group, after the first page. */
  extraRows: Record<string, ItemRow[]>;
  cursors: Record<string, string | null>;
  loadingMore: Record<string, boolean>;
  saved: TableViewSettings;
  effective: TableViewSettings;
  dirty: boolean;
  selection: GridSelection;
  itemSelection: ItemSelection;
  editing: { address: CellAddress; initialInput?: string } | null;
  undo: UndoStack;
  pane: PaneKind;
  /** P-111: the board the open pane acts on; `null` means this board. */
  paneBoardId: BoardId | null;
  /**
   * **P-326.** Where the control that opened the pane is, when it is not the board's own `+`.
   * The nested subitems header's `+` opens the gallery **under itself** `[uis:20-nested-add-column]`;
   * without it the gallery anchored to the parent header's `+` and read as the parent board's.
   */
  paneAnchor: { x: number; y: number } | null;
  menu: MenuState | null;
  facets: Record<string, FacetValue[]>;
  toast: ToastState | null;
  /** Set while a drag is in flight, so realtime patches are queued instead of applied (§8.12). */
  interacting: boolean;
  /**
   * **P-054.** The rows `Move to ▸ Move to board` is picking a destination for. The dialog is
   * rendered by `TableView`, not by `ItemMenu`: the row's `onSelect` closes the menu in the
   * same tick, so a dialog owned by the menu was unmounted before it could ever paint.
   */
  movingItemIds: readonly ItemId[];
  /**
   * **P-080.** The column whose `Rename` dialog is open. It lives here and not in `ColumnMenu`
   * for the same reason `movingItemIds` does: `Menu` closes the menu for every row without a
   * submenu, so `actions.openMenu(null)` unmounts the menu — and any `useState` inside it —
   * in the very tick the row's `onSelect` sets it. The dialog is rendered by `TableView`.
   */
  renamingColumnId: ColumnId | null;
  /** **P-087.** The column whose delete confirm is open, hoisted for exactly the same reason. */
  deletingColumnId: ColumnId | null;
  /**
   * **P-082.** The column `Add column to the right` was opened from. `addColumn` anchors the
   * new column after it; the grid's own `+` leaves it `null` and still appends at the end.
   */
  addColumnAfterId: ColumnId | null;
  /**
   * **P-090.** Set while `window.print()` runs: the grid renders **every** row instead of the
   * ~19 the virtualiser keeps in the DOM, which is the whole difference between printing a
   * board and printing a screenshot of one.
   */
  printing: boolean;

  /* ── subitems (M3.S1 §6) ─────────────────────────────────────────────────── */
  /**
   * The parents whose nested table is open. monday remembers this across a reload and exposes
   * nothing about it on the API (`[uis]` §7, q-S1-6), so it is per user and client-side.
   */
  expandedItemIds: readonly ItemId[];
  /** The subitems board's columns — one set for the whole board, loaded with the first expand. */
  subitemColumns: readonly BoardColumn[];
  /** P-111: the subitems **board**'s id, for the nested header's `+`. `null` until it exists. */
  subitemsBoardId: BoardId | null;
  /** `parentItemId -> subitem rows`, in position order. */
  subitemsByParent: Record<string, readonly ItemRow[]>;
  /**
   * **P-308.** The api's own sentence for a subitems read that was refused — on the pilot,
   * *"Board 6061227186 not found"*, because a duplicated board pointed at somebody else's
   * subitems board (P-307). It used to reach `load()`'s `catch` and become the TableView's
   * **error state**, so an unreachable subitems board took the whole board page down. monday
   * never fails a board over its subitems: the read fails, the parents stay **collapsed**, the
   * rows that were asked for are marked, and the board is on screen.
   *
   * `null` while nothing has been refused; cleared by the first read that succeeds.
   */
  subitemsError: string | null;
  /** The parents a refused read was asked for — the rows that carry the muted line. */
  subitemsUnavailableFor: readonly ItemId[];

  /* ── realtime (M0-F5 §6, §8) ─────────────────────────────────────────────── */
  /** Who else is on this board, from the last roster broadcast. `null` = not live. */
  presence: PresenceRoster | null;
  /** Set when the board is archived or deleted under the viewer (F5 §8, E8). */
  removedState: 'archived' | 'deleted' | null;
  /** Set on `room:revoked`: the viewer lost read access mid-session (E7). */
  accessRevoked: boolean;

  load(): Promise<void>;
  loadMore(groupId: GroupId): Promise<void>;
  setEffective(patch: Partial<TableViewSettings>): void;
  resetPersonal(): void;
  saveView(): Promise<void>;
  refreshFacets(): Promise<void>;

  setSelection(next: GridSelection): void;
  setItemSelection(next: ItemSelection): void;
  startEditing(address: CellAddress, initialInput?: string): void;
  stopEditing(): void;

  commitCell(address: CellAddress, value: unknown): Promise<void>;
  clearSelection(): Promise<void>;
  copySelection(): Promise<string>;
  paste(text: string): Promise<void>;
  /** No id: the top of the stack (`Ctrl/Cmd+Z`). With one: that entry (the toast's button). */
  undoLast(entryId?: string): Promise<void>;
  /**
   * P-105 — `Ctrl/Cmd+Y` and `Ctrl/Cmd+Shift+Z`: re-applies the entry the last undo inverted,
   * as a **fresh** mutation (`is_undo_action: false`, `[ba§2]`).
   */
  redoLast(): Promise<void>;

  renameItem(itemId: ItemId, name: string): Promise<void>;
  /**
   * P-057: `placement` is monday's own placement object as well as a side, because
   * `Create new item below` inserts **after this row** and not at the end of the group.
   */
  addItem(groupId: GroupId, name: string, placement?: ItemPlacement): Promise<void>;
  /**
   * **P-291.** One item per pasted line, appended to the group in the pasted order — what
   * monday's own `+ Add item` input does with a multi-line paste (measured:
   * `discovery/behavior-notes/P-291-paste-creates-items.md`). It is its own action and not a
   * loop in the view, because the grid must repaint **once** at the end and not per row.
   */
  addItems(groupId: GroupId, names: readonly string[], placement?: ItemPlacement): Promise<number>;
  /**
   * **P-304 — where the rows live.** A subitem is an item on the board's linked *subitems*
   * board `[S1§2]`, so a removal, an archive or a duplicate of one has to name that board and
   * the parent whose nested table it belongs to. Absent = this board's own rows, as before.
   */
  deleteItems(itemIds: readonly ItemId[], target?: RowTarget): Promise<void>;
  archiveItems(itemIds: readonly ItemId[], target?: RowTarget): Promise<void>;
  duplicateItems(itemIds: readonly ItemId[], target?: RowTarget): Promise<void>;
  /** The subitem row menu's **Move to Item ▸** — `items.moveSubitemToParent` `[uis:05]`. */
  moveSubitemToParent(subitemId: ItemId, parentItemId: ItemId, fromParentItemId: ItemId): Promise<void>;
  /** The subitem row menu's **Convert to item** — `items.convertSubitemToItem` `[uis:05]`. */
  convertSubitemToItem(subitemId: ItemId, fromParentItemId: ItemId): Promise<void>;
  toggleGroupCollapsed(groupId: GroupId | 'all', collapsed: boolean): Promise<void>;
  addGroup(title?: string, placement?: GroupPlacement): Promise<void>;
  renameGroup(groupId: GroupId, title: string): Promise<void>;
  setGroupColor(groupId: GroupId, color: string): Promise<void>;
  /** P-058: monday's submenu is `Duplicate items` / `Duplicate items & updates`. */
  duplicateGroup(groupId: GroupId, withItems: boolean, withUpdates?: boolean): Promise<void>;
  moveGroup(groupId: GroupId, placement: GroupPlacement): Promise<void>;
  archiveGroup(groupId: GroupId): Promise<void>;
  deleteGroup(groupId: GroupId): Promise<void>;
  moveItems(itemIds: readonly ItemId[], groupId: GroupId): Promise<void>;
  /** Open or close one parent's nested table (U3, U11). */
  toggleSubitems(itemId: ItemId): Promise<void>;
  /** The toolbar's "Expand all" / "Collapse all" — every loaded row with subitems. */
  setAllSubitemsExpanded(expanded: boolean): Promise<void>;
  /** `+ Add subitem` (U10). **P-308**: `false` when the api refused it (and toasted). */
  addSubitem(parentItemId: ItemId, name: string): Promise<boolean>;
  /**
   * **P-266** — the `+` beside the row's name. The same creation as `addSubitem`, plus the
   * half that makes it "in place": the parent's nested table is opened first, so the new
   * subitem appears under the row the reader clicked instead of only bumping a count bubble.
   */
  addSubitemInPlace(parentItemId: ItemId): Promise<void>;
  reorderItem(itemId: ItemId, groupId: GroupId, afterItemId: ItemId | null): Promise<void>;
  /**
   * P-103 — a drag that picked up a **multi-row selection**: every row lands at the drop
   * point, in the order it had on the board, as one mutation.
   */
  reorderItems(itemIds: readonly ItemId[], groupId: GroupId, afterItemId: ItemId | null): Promise<void>;

  addColumn(type: string, title: string): Promise<void>;
  renameColumn(columnId: ColumnId, title: string): Promise<void>;
  resizeColumn(columnId: ColumnId, width: number): Promise<void>;
  hideColumn(columnId: ColumnId, hidden: boolean): void;
  pinColumn(columnId: ColumnId, pinned: boolean): void;
  /** 6i / q455: `Wrap text`. Per column, and it grows every row to 56 px (see `settings.ts`). */
  wrapColumn(columnId: ColumnId, wrapped: boolean): void;
  moveColumn(columnId: ColumnId, beforeColumnId: ColumnId | null): Promise<void>;
  /**
   * P-087: the delete itself. The **confirm** is raised by `ColumnDeleteConfirm` (the shell's
   * `confirm()` is a React context and the store is not a component), and this then deletes,
   * pushes the undo entry and raises the toast whose `Undo` is `columns.restore`.
   */
  deleteColumn(columnId: ColumnId): Promise<void>;
  /** P-084: `columns.duplicate` — a copy beside the original, with the board reloaded. */
  duplicateColumn(columnId: ColumnId): Promise<void>;
  setAggregation(columnId: ColumnId, aggregation: ColumnAggregation): Promise<void>;
  /**
   * **P-129.** Answers whether the settings were actually written, so the dialog can stay open
   * on a refusal instead of closing over a change that never happened. A stale `revision`
   * (`CONFLICT`) is the one failure with its own wording and its own recovery: the board is
   * reloaded so the dialog re-opens on the settings that really are stored.
   */
  updateColumnSettings(columnId: ColumnId, settings: unknown): Promise<boolean>;

  /**
   * P-111: `boardId` names the board a board-scoped pane acts on. The add-column gallery is
   * board-agnostic — only its *implicit* board was the parent one — so the nested subitems
   * header's `+` opens the same gallery against the subitems board.
   */
  openPane(pane: PaneKind, boardId?: BoardId | null, anchor?: { x: number; y: number } | null): void;
  /** P-082: the add-column gallery, anchored to the column whose menu opened it. */
  openAddColumnAfter(columnId: ColumnId): void;
  openMenu(menu: MenuState | null): void;
  /** P-054: open (`itemIds`) or close (`[]`) the board picker. */
  setMovingItems(itemIds: readonly ItemId[]): void;
  /** P-080: open (`columnId`) or close (`null`) the column rename dialog. */
  setRenamingColumn(columnId: ColumnId | null): void;
  /** P-087: open (`columnId`) or close (`null`) the column delete confirm. */
  setDeletingColumn(columnId: ColumnId | null): void;
  /** P-090: render every row, print, and put the virtualiser back. */
  printBoard(): Promise<void>;
  showToast(toast: Omit<ToastState, 'id'>): void;
  dismissToast(): void;
  setInteracting(value: boolean): void;

  /** Apply one realtime patch (M0-F5 §5.2). Queued while a drag is in flight (§8.12). */
  applyRemotePatch(patch: StorePatch): void;
  /** `true` while this client's own optimistic write with that id is unsettled (§5.3). */
  isOwnMutation(mutationId: string): boolean;
  setPresence(roster: PresenceRoster | null): void;
  setAccessRevoked(): void;
}

export type TableStore = UseBoundStore<StoreApi<TableState>>;

let toastSequence = 0;

/**
 * M3.S1 §4: the expanded set survives a reload on monday and the API exposes nothing about it
 * (q-S1-6), so it lives in `localStorage`, per (board, view). Every access is guarded: a
 * private window, cleared site data or a browser that blocks storage must not break the grid.
 */
const expandedKey = (boardId: string, viewId: ViewId): string =>
  `ez.subitems.expanded.${boardId}.${String(viewId)}`;

export function readExpanded(boardId: string, viewId: ViewId): ItemId[] {
  try {
    const raw = globalThis.localStorage?.getItem(expandedKey(boardId, viewId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed.filter((id) => typeof id === 'string') as ItemId[]) : [];
  } catch {
    return [];
  }
}

export function writeExpanded(boardId: string, viewId: ViewId, ids: readonly ItemId[]): void {
  try {
    globalThis.localStorage?.setItem(expandedKey(boardId, viewId), JSON.stringify(ids));
  } catch {
    /* storage is a convenience here, never a requirement */
  }
}

export function createTableStore(source: BoardDataSource, boardId: string, viewId: ViewId): TableStore {
  return create<TableState>((set, get) => {
    /** The rows the grid currently holds for a group: first page + everything paged in. */
    const rowsOf = (groupId: string): ItemRow[] => {
      const state = get();
      const group = state.payload?.groups.find((entry) => entry.id === groupId);
      return [...(group?.page.items ?? []), ...(state.extraRows[groupId] ?? [])];
    };

    /*
     * **P-328 — paging bookkeeping.** `generation` counts the payloads `reload()` has painted: a
     * page fetched against an older payload continues rows that are no longer on screen, so its
     * answer is dropped instead of being appended after the fresh first page. `inflight` holds
     * the one running page request per group, so a second caller (the grid's trigger while a
     * reload is paging back) waits for it instead of returning empty-handed.
     */
    let generation = 0;
    const inflight = new Map<string, Promise<'paged' | 'done' | 'failed'>>();
    /** Per group, the depth a running `reload()` is still paging back to. */
    const restoring = new Map<string, number>();
    const MAX_PAGES_PER_WALK = 20;

    /**
     * One page for one group. Never reloads: `'failed'` is the caller's to act on, which is what
     * keeps a failing page inside `reload()` from reloading again.
     */
    const fetchPage = (groupId: GroupId): Promise<'paged' | 'done' | 'failed'> => {
      const running = inflight.get(groupId);
      if (running) return running;
      const cursor = get().cursors[groupId];
      if (!cursor) return Promise.resolve('done');
      const started = generation;
      set((current) => ({ loadingMore: { ...current.loadingMore, [groupId]: true } }));
      const request = async (): Promise<'paged' | 'done' | 'failed'> => {
        try {
          const page = await source.page({
            boardId: boardId as never,
            viewId,
            groupId,
            cursor,
            state: queryState(),
          });
          if (generation !== started) return 'done';
          set((current) => ({
            extraRows: {
              ...current.extraRows,
              [groupId]: [...(current.extraRows[groupId] ?? []), ...page.items],
            },
            cursors: { ...current.cursors, [groupId]: page.cursor },
            loadingMore: { ...current.loadingMore, [groupId]: false },
          }));
          return 'paged';
        } catch {
          if (generation !== started) return 'done';
          set((current) => ({ loadingMore: { ...current.loadingMore, [groupId]: false } }));
          return 'failed';
        }
      };
      const run: Promise<'paged' | 'done' | 'failed'> = request().finally(() => {
        if (inflight.get(groupId) === run) inflight.delete(groupId);
      });
      inflight.set(groupId, run);
      return run;
    };

    /**
     * Pages one group forward until `held()` says enough, the cursor is gone, a page fails, or
     * a newer reload replaced the rows — capped, so a cursor that never ends cannot spin.
     * Shared by P-324 (`revealRow`) and P-328 (`reload()` keeping the paging depth).
     */
    const pageUntil = async (groupId: GroupId, held: () => boolean): Promise<void> => {
      const started = generation;
      for (let pages = 0; pages < MAX_PAGES_PER_WALK; pages += 1) {
        if (generation !== started || held()) return;
        if (!get().cursors[groupId]) return;
        if ((await fetchPage(groupId)) !== 'paged') return;
      }
    };

    /**
     * **P-324.** A row created at the bottom of a group longer than the first page is not in
     * the page `reload()` paints (Michael: "whenever we try to add a new entry, it just
     * disappears"). Page the group forward until the row is on screen — or the group is
     * exhausted, which cannot happen for a row the api just answered with.
     */
    const revealRow = (groupId: GroupId, itemId: ItemId): Promise<void> =>
      pageUntil(groupId, () => rowsOf(groupId).some((row) => row.id === itemId));

    const allRows = (): ItemRow[] => {
      const state = get();
      return (state.payload?.groups ?? []).flatMap((group) => rowsOf(group.id));
    };

    const renderContext = (): CellRenderContext => ({
      people: get().payload?.people ?? [],
      /** P-122: the same account tag vocabulary the grid renders with. */
      tags: get().payload?.tags ?? [],
      ...(source.createTag
        ? { createTag: (name: string) => source.createTag!({ boardId: boardId as never, name }) }
        : {}),
      columns: get().payload?.columns ?? [],
      search: get().effective.search,
      timezone: 'UTC',
    });

    const geometry = (): GridGeometry => {
      const state = get();
      const columns = state.payload?.columns ?? [];
      const { pinned, scrolling } = visibleColumns(columns, state.effective, NAME_COLUMN_ID);
      return {
        rows: (state.payload?.groups ?? [])
          .filter((group) => !group.collapsed)
          .flatMap((group) => rowsOf(group.id).map((row) => ({ itemId: row.id, groupId: group.id }))),
        columnIds: [...pinned, ...scrolling].map((column) => column.id),
      };
    };

    const columnOf = (columnId: ColumnId): BoardColumn | undefined =>
      get().payload?.columns.find((column) => column.id === columnId);

    const itemOf = (itemId: ItemId): ItemRow | undefined => allRows().find((row) => row.id === itemId);

    /**
     * **P-130** — "can this column hold a value at all?", asked of the **registry** and never of
     * a `switch (type)` (CLAUDE.md rule 6).
     *
     * Two different things make a cell unwritable and the grid used to know only one of them.
     * `permissions.readOnlyColumnIds` is the per-board grant; a column that is read-only by
     * **type** — an id, an auto-number, a creation log, a last-updated stamp, a progress
     * battery, a formula, a mirror — is not in that list, so `Backspace` over a selection that
     * included one sent `cells.setMany` and was answered `400 Column <id> is read-only`, once
     * per computed column, each one in the user's console. monday greys those columns and never
     * sends the request.
     */
    const writableColumn = (columnId: ColumnId): boolean => {
      if (columnId === NAME_COLUMN_ID) return true;
      const column = columnOf(columnId);
      if (!column) return false;
      if ((get().payload?.permissions.readOnlyColumnIds ?? []).includes(columnId)) return false;
      return findColumnType(column.type)?.writable !== false;
    };

    /**
     * **P-263** — the rows one editor commit writes.
     *
     * monday's grid treats a ticked row selection as the *scope* of an edit: change a column on
     * any **selected** item and every selected item gets that value, logged as one
     * `batch_change_pulses_column_value` batch. Everything else keeps the single-row path —
     * nothing ticked, one row ticked, or an edit on a row that is **not** part of the
     * selection (monday leaves the selection alone and writes only the row that was edited).
     *
     * Three gates, all asked of a registry and never of a `switch (type)` (CLAUDE.md rule 6):
     * the column must be writable here (`writableColumn`, P-130 — a computed column or a
     * read-only grant is out), it must not be the name (P-052: `items.name` is its own store
     * and every item's name is its own), and its type must declare `massEditable` — the value
     * means the same thing on every item. An unticked-but-loaded row is never touched, and a
     * selected id the grid has not paged in is dropped rather than written blind.
     *
     * Returns `[]` when the edit is a single-row one; the caller then writes exactly the cell
     * that was edited.
     */
    const massEditTargets = (address: CellAddress): ItemId[] => {
      const selected = get().itemSelection.ids;
      if (selected.length < 2) return [];
      if (!selected.some((id) => String(id) === String(address.itemId))) return [];
      if (address.columnId === NAME_COLUMN_ID) return [];
      if (!writableColumn(address.columnId)) return [];
      const column = columnOf(address.columnId);
      if (!column || getColumnUi(column.type).massEditable !== true) return [];
      const loaded = new Set(allRows().map((row) => String(row.id)));
      const targets = selected.filter((id) => loaded.has(String(id)));
      return targets.length > 1 ? targets : [];
    };

    /**
     * What the read needs from the client. `collapsedGroupIds` is deliberately **not** sent:
     * it is per-user state the source owns (`view_user_state`, §5.6), and echoing our stale
     * copy back would undo the collapse we just asked for.
     */
    const queryState = (): Partial<QueryState> => {
      const settings = get().effective;
      return {
        filter: settings.filter,
        sort: settings.sort,
        search: settings.search,
        groupBy: settings.groupBy,
        hiddenColumnIds: settings.hiddenColumnIds,
      };
    };


    /**
     * §8.4: a cell edit paints **immediately** and is reconciled afterwards. The write is
     * applied to the loaded rows before the request leaves, so the grid never shows a stale
     * value for a round trip; `rollbackCells` puts the old values back when the server
     * refuses, and the reload that follows a success is what brings the group summaries and
     * any server-side derivation (`last_updated`, formulas) up to date.
     */
    const patchCells = (writes: readonly CellWrite[]): CellWrite[] => {
      if (writes.length === 0) return [];
      const previous: CellWrite[] = [];
      const byItem = new Map<string, Map<string, unknown>>();
      for (const write of writes) {
        const columns = byItem.get(String(write.itemId)) ?? new Map<string, unknown>();
        columns.set(String(write.columnId), write.value);
        byItem.set(String(write.itemId), columns);
      }

      const applyTo = (row: ItemRow): ItemRow => {
        const columns = byItem.get(String(row.id));
        if (!columns) return row;
        const values = { ...row.values };
        let name = row.name;
        for (const [columnId, value] of columns) {
          previous.push({
            itemId: row.id,
            columnId: columnId as ColumnId,
            value: columnId === String(NAME_COLUMN_ID) ? row.name : (row.values[columnId] ?? null),
          });
          if (columnId === String(NAME_COLUMN_ID)) name = String(value ?? '');
          else values[columnId] = value;
        }
        return { ...row, name, values };
      };

      set((current) => ({
        payload: current.payload
          ? {
              ...current.payload,
              groups: current.payload.groups.map((group) => ({
                ...group,
                page: { ...group.page, items: group.page.items.map(applyTo) },
              })),
            }
          : current.payload,
        extraRows: Object.fromEntries(
          Object.entries(current.extraRows).map(([groupId, rows]) => [groupId, rows.map(applyTo)]),
        ),
      }));
      return previous;
    };

    /** Puts back exactly what `patchCells` reported, with no second round trip. */
    const rollbackCells = (previous: readonly CellWrite[]) => {
      if (previous.length > 0) patchCells(previous);
    };

    /**
     * **P-277.** What the *answer* to a cell write does to the grid — for every path that
     * writes cells, not just `commitCell`.
     *
     * `paste`, `clearSelection` and the two undo paths painted their own guess of the value
     * (or, for an undo, nothing at all) and left the real repaint to the `reload()` at the end
     * of the action. That made the slowest possible round trip responsible for a value the
     * **response already carried**, and anything that kept the reload from landing left the
     * cell showing what nobody wrote — the reported defect: `cells.setMany` answered
     * `changed: true`, the toast said *"Pasted 1 cell"*, and the cell stayed empty until F5.
     *
     * So the written cells are applied from `result.written`, which is the server's own
     * normalised value (the same value `boards.read` would answer, not the client's reading of
     * the clipboard), and every cell that was painted optimistically but is **not** in
     * `written` — refused by the service, or stopped by the no-op gate (B5 §5.1) — is put back
     * to what it held. The `reload()` that follows is then only what it was always meant to be:
     * the group summaries, `last_updated` and the server-side derivations.
     */
    const applyWriteResult = (result: CellWriteResult, painted: readonly CellWrite[]) => {
      if (result.written.length > 0) patchCells(result.written);
      if (painted.length === 0) return;
      const written = new Set(result.written.map((write) => cellKey(write.itemId, write.columnId)));
      const reverted = painted.filter((cell) => !written.has(cellKey(cell.itemId, cell.columnId)));
      rollbackCells(reverted);
    };

    /*
     * Realtime plumbing. A structural patch schedules one reload per window rather than one
     * per event: an automation chain writing 30 rows is 30 envelopes and one `boards.read`.
     */
    const REMOTE_RELOAD_WINDOW_MS = 120;
    let reloadTimer: ReturnType<typeof setTimeout> | null = null;
    const queuedPatches: StorePatch[] = [];

    const scheduleReload = () => {
      if (reloadTimer !== null) return;
      reloadTimer = setTimeout(() => {
        reloadTimer = null;
        void reload().catch(() => {
          // A failed background reload leaves the board as it was; the next event retries.
        });
      }, REMOTE_RELOAD_WINDOW_MS);
    };

    /**
     * §5.3's state machine. The callbacks are the store's: rolling back is `rollbackCells`
     * with exactly what `patchCells` reported, and the toast is the store's toast.
     */
    const optimistic = new OptimisticTracker<readonly CellWrite[]>({
      rollback: (previous) => rollbackCells(previous),
      onError: () =>
        get().showToast({ message: "Couldn't save — try again", kind: 'error' }),
      // O4: the write did land and the response was lost; the toast was wrong.
      withdrawError: () => set({ toast: null }),
    });
    // O5: one interval, not one timeout per mutation — the resolution needed is seconds.
    const sweepTimer = setInterval(() => optimistic.sweep(), 2_000);
    if (typeof sweepTimer === 'object' && 'unref' in sweepTimer) sweepTimer.unref?.();

    const flushQueuedPatches = () => {
      if (queuedPatches.length === 0) return;
      const pending = queuedPatches.splice(0, queuedPatches.length);
      for (const patch of pending) get().applyRemotePatch(patch);
    };

    const reload = async () => {
      const payload = await source.load({
        boardId: boardId as never,
        viewId,
        state: queryState(),
      });
      /*
       * **P-328.** Every write ends in this reload, and it used to throw away every page the
       * user had paged into (`extraRows: {}`, cursors from page 1): a cell write on row 60 of a
       * 70-row group repainted the group with 50 rows. So the depth each group held — or the
       * depth an overlapping reload is still paging back to — is recorded before the payload is
       * replaced, and paged back to once the first page is painted.
       */
      const depth = new Map<string, number>();
      for (const group of get().payload?.groups ?? []) {
        depth.set(group.id, Math.max(rowsOf(group.id).length, restoring.get(group.id) ?? 0));
      }
      generation += 1;
      inflight.clear();
      set({
        payload,
        saved: payload.view.settings,
        effective: payload.view.effectiveSettings,
        dirty: isDirty(payload.view.settings, payload.view.effectiveSettings),
        extraRows: {},
        cursors: Object.fromEntries(payload.groups.map((group) => [group.id, group.page.cursor])),
        loadingMore: {},
        status: 'ready',
        error: null,
      });
      const painted = generation;
      restoring.clear();
      const behind = payload.groups.filter(
        (group) => group.page.cursor && (depth.get(group.id) ?? 0) > group.page.items.length,
      );
      for (const group of behind) restoring.set(group.id, depth.get(group.id)!);
      await Promise.all(
        behind.map((group) =>
          pageUntil(group.id, () => rowsOf(group.id).length >= depth.get(group.id)!),
        ),
      );
      if (generation === painted) restoring.clear();
      // M3.S1: a reload must refresh whatever is expanded, or the nested rows go stale.
      const open = get().expandedItemIds;
      if (open.length > 0) await loadSubitems(open);
      await forgetStaleRefs(payload);
    };

    /**
     * **P-268.** The read answered; some of what we asked for named a column that is gone.
     *
     * The board is on screen — `boards.read` drops such an entry instead of refusing the whole
     * request now — so the only thing left to do is **forget** it. Without this the browser
     * would send the same dead sort on every load for the rest of the board's life, the
     * toolbar would keep counting `Sort / 4` for four rules that do nothing, and the pane would
     * offer a rule whose column has no title to render.
     *
     * monday drops these silently, so there is no toast: the state simply shrinks. The stored
     * per-user overlay is rewritten because it is the copy that would resurrect the reference;
     * the *view's* settings are pruned only in memory — writing them back is "Save to this
     * view", which is the owner's decision and not ours (§5.6).
     */
    const forgetStaleRefs = async (payload: BoardViewPayload) => {
      const ignored = payload.ignored ?? [];
      if (ignored.length === 0) return;
      const current = get();
      const effective = pruneStaleColumnRefs(current.effective, ignored);
      const saved = pruneStaleColumnRefs(current.saved, ignored);
      if (effective === current.effective && saved === current.saved) return;
      set({ effective, saved, dirty: isDirty(saved, effective) });
      if (effective === current.effective) return;
      try {
        await source.setPersonalState({ viewId, state: effective });
      } catch {
        // A failed write leaves the dead reference stored; the next load prunes it again.
      }
    };

    /**
     * Applies a settings change: the personal state is written (debounced by the caller for
     * typing surfaces) and the board is re-read, because filtering and sorting are the
     * server's job — the client never filters a page it does not fully hold (§5.3).
     */
    const applySettings = async (patch: Partial<TableViewSettings>) => {
      const next = { ...get().effective, ...patch };
      set({ effective: next, dirty: isDirty(get().saved, next) });
      await source.setPersonalState({ viewId, state: next });
      await reload();
      void get().refreshFacets();
    };

    /**
     * Load (or refresh) the nested rows of some parents. One call for the whole set — never
     * one per row (`specs/M3-S1-subitems.md` §5.3) — and the subitems board's columns come
     * back with it, because the board is created lazily and may not have existed a moment ago.
     */
    const loadSubitems = async (parentItemIds: readonly ItemId[]) => {
      if (parentItemIds.length === 0) return true;
      let read;
      try {
        read = await source.subitemsOf({ boardId: boardId as never, parentItemIds });
      } catch (error) {
        /*
         * **P-308.** A refused subitems read is not a refused board. It used to be awaited with
         * no catch on the load path — `reload()` re-reads whatever is expanded, including the
         * set remembered from the last visit — so `load()`'s catch turned it into the
         * TableView's error state and the grid was replaced by *"Board … not found"*.
         *
         * monday never fails a board over its subitems. The parents are left **collapsed**, the
         * ones that were asked for carry a muted line with the api's own sentence, and the
         * toast is raised **once** per sentence rather than once per row or once per reload.
         */
        const message =
          error instanceof Error && error.message.trim() !== ''
            ? error.message
            : 'Subitems could not be loaded';
        const current = get();
        const open = new Set(current.expandedItemIds.map(String));
        const asked = parentItemIds.map(String);
        for (const id of asked) open.delete(id);
        const collapsed = current.expandedItemIds.filter((id) => open.has(String(id)));
        const marked = new Set(current.subitemsUnavailableFor.map(String));
        for (const id of asked) marked.add(id);
        set({
          expandedItemIds: collapsed,
          subitemsError: message,
          subitemsUnavailableFor: [...marked] as ItemId[],
        });
        writeExpanded(boardId, viewId, collapsed);
        if (current.subitemsError !== message) get().showToast({ message, kind: 'error' });
        return false;
      }
      const current = get();
      const cleared = new Set(parentItemIds.map(String));
      set({
        subitemColumns: read.columns.length > 0 ? read.columns : current.subitemColumns,
        subitemsBoardId: read.boardId ?? current.subitemsBoardId,
        subitemsByParent: { ...current.subitemsByParent, ...read.byParent },
        // A read that answers clears the mark on exactly the rows it answered for.
        subitemsUnavailableFor: current.subitemsUnavailableFor.filter(
          (id) => !cleared.has(String(id)),
        ),
        subitemsError:
          current.subitemsUnavailableFor.every((id) => cleared.has(String(id)))
            ? null
            : current.subitemsError,
      });
      return true;
    };

    /**
     * **P-326.** The parent whose nested table holds `itemId`, or `null` for an item row. A
     * subitem's cells are columns of the **subitems board** (`subitemColumns`), and its writes
     * go there: resolving them against the parent board's columns is what left a subitem's
     * Status inert (the parent had no column of that id, so no editor opened) and its People
     * pick unwritten (the write named the parent board, which does not hold the row).
     */
    const subitemParentOf = (itemId: ItemId): string | null => {
      for (const [parentId, rows] of Object.entries(get().subitemsByParent)) {
        if (rows.some((row) => String(row.id) === String(itemId))) return parentId;
      }
      return null;
    };

    /** Paints `writes` into one parent's nested rows; answers the rows as they were. */
    const patchSubitemCells = (
      parentId: string,
      writes: readonly CellWrite[],
    ): readonly ItemRow[] | undefined => {
      const before = get().subitemsByParent[parentId];
      if (!before) return undefined;
      const next = before.map((row) => {
        const mine = writes.filter((write) => String(write.itemId) === String(row.id));
        if (mine.length === 0) return row;
        const values = { ...row.values };
        for (const write of mine) values[write.columnId] = write.value;
        return { ...row, values };
      });
      set((current) => ({ subitemsByParent: { ...current.subitemsByParent, [parentId]: next } }));
      return before;
    };

    const restoreSubitemRows = (parentId: string, rows: readonly ItemRow[] | undefined) => {
      if (!rows) return;
      set((current) => ({ subitemsByParent: { ...current.subitemsByParent, [parentId]: rows } }));
    };

    /**
     * **P-326.** One subitem cell, written through `cells.setMany` on the **subitems board**,
     * painted at once and repainted from the server's read of the nested table. The toast and
     * its Undo are the item cell's own (`commitCell`), naming the subitems board so the inverse
     * reaches the same row.
     */
    const commitSubitemCell = async (
      address: CellAddress,
      value: unknown,
      parentId: string,
    ): Promise<void> => {
      const target = get().subitemsBoardId;
      if (target === null) {
        get().showToast({ message: 'The subitems board is not loaded yet', kind: 'error' });
        return;
      }
      const writes: CellWrite[] = [{ itemId: address.itemId, columnId: address.columnId, value }];
      const before = patchSubitemCells(parentId, writes);
      let result: CellWriteResult;
      try {
        result = await source.setCellValues({ boardId: target as never, writes });
      } catch (error) {
        restoreSubitemRows(parentId, before);
        get().showToast({
          message: error instanceof Error ? error.message : 'The cell could not be saved',
          kind: 'error',
        });
        return;
      }
      if (result.written.length > 0) {
        const id = nextUndoId('edit');
        set((current) => ({
          undo: pushUndo(current.undo, {
            kind: 'cells',
            id,
            label: 'Cell updated',
            inverse: result.undo,
            applied: result.written,
            at: Date.now(),
            boardId: String(target),
            parentItemId: parentId,
          }),
        }));
        get().showToast({ message: 'Cell updated', actionLabel: 'Undo', kind: 'undo', undoEntryId: id });
      }
      if (result.skipped.length > 0) {
        restoreSubitemRows(parentId, before);
        get().showToast({
          message: `1 cell skipped (${result.skipped[0]!.reason})`,
          kind: 'error',
        });
      }
      await loadSubitems([parentId as ItemId]);
    };

    /**
     * **P-052**: the one writer of an item's name from the grid.
     *
     * `items.name` is the name's store — `cells.setMany` skips the `name` column on purpose
     * ("the item name is not a `cell_values` row") — so every surface that used to send the
     * name through `setCellValues` wrote nothing at all and kept the optimistic paint. This
     * paints optimistically the same way, then calls `items.rename` and rolls the paint back
     * if the service refuses it. An empty or unchanged name writes nothing: monday neither
     * stores a blank item name nor logs a rename that changed nothing.
     */
    const renameFromGrid = async (itemId: ItemId, value: unknown): Promise<boolean> => {
      const name = typeof value === 'string' ? value : String(value ?? '');
      const writes: CellWrite[] = [{ itemId, columnId: NAME_COLUMN_ID, value: name }];
      const previous = patchCells(writes);
      const before = String(previous[0]?.value ?? '');
      if (name.trim() === '' || name === before) {
        rollbackCells(previous);
        return false;
      }
      /*
       * **P-120.** The rollback and the error toast belong to a **refused write** and to
       * nothing else. A throw *after* `renameItem` resolved is a client-side bug (the mapper's
       * was the one this card is named for), and reporting it as "the server refused the name"
       * rolled a successful rename back under the user and toasted a failure that never
       * happened. So the call and everything after it are separated: only the call rolls back.
       */
      try {
        await source.renameItem({ boardId: boardId as never, itemId, name });
      } catch (error) {
        if (isPostWriteError(error)) {
          // The rename landed; only the client-side mapping of the answer failed. Repaint from
          // the server rather than telling the user a successful write was refused.
          console.error(`items.rename succeeded but its answer could not be read: ${error.message}`);
          await reload();
          return true;
        }
        rollbackCells(previous);
        get().showToast({
          message: error instanceof Error ? error.message : 'That name could not be saved',
          kind: 'error',
        });
        return false;
      }
      await reload();
      return true;
    };

    /**
     * The one row-removal path (P-050). It records the action the service reports so the
     * toast's **Undo** can restore exactly that set, clears the row selection, reloads, and
     * raises the toast bound to its own undo entry.
     */
    const removeItems = async (
      itemIds: readonly ItemId[],
      kind: 'delete' | 'archive',
      target?: RowTarget,
    ) => {
      if (itemIds.length === 0) return;
      // P-304: a subitem's rows are on the subitems board; everything else is on this one.
      const onBoard = (target?.boardId ?? boardId) as never;
      let result;
      try {
        result =
          kind === 'delete'
            ? await source.deleteItems({ boardId: onBoard, itemIds })
            : await source.archiveItems({ boardId: onBoard, itemIds });
      } catch (error) {
        get().showToast({
          message:
            error instanceof Error
              ? error.message
              : kind === 'delete'
                ? 'Those items could not be deleted'
                : 'Those items could not be archived',
          kind: 'error',
        });
        return;
      }
      const removed = result.itemIds.length > 0 ? result.itemIds : itemIds;
      const id = nextUndoId(kind);
      set((current) => ({
        itemSelection: EMPTY_ITEM_SELECTION,
        undo: pushUndo(current.undo, {
          kind: kind === 'delete' ? 'restore' : 'unarchive',
          id,
          label: kind === 'delete' ? deleteLabel(removed.length) : archiveLabel(removed.length),
          itemIds: removed,
          actionRecordUuid: result.actionRecordUuid,
          at: Date.now(),
          // P-304: the undo has to reach the same board, and repaint the same parent.
          ...(target ? { boardId: String(target.boardId), parentItemId: String(target.parentItemId) } : {}),
        }),
      }));
      await reload();
      // P-304: the nested table the row left, and the parent's count bubble with it.
      if (target) await loadSubitems([target.parentItemId]);
      get().showToast({
        message: kind === 'delete' ? deleteLabel(removed.length) : archiveLabel(removed.length),
        actionLabel: 'Undo',
        kind: 'undo',
        undoEntryId: id,
      });
    };

    return {
      boardId,
      viewId,
      status: 'loading',
      error: null,
      payload: null,
      extraRows: {},
      cursors: {},
      loadingMore: {},
      saved: DEFAULT_TABLE_VIEW_SETTINGS,
      effective: DEFAULT_TABLE_VIEW_SETTINGS,
      dirty: false,
      selection: EMPTY_SELECTION,
      itemSelection: EMPTY_ITEM_SELECTION,
      editing: null,
      undo: EMPTY_UNDO_STACK,
      pane: null,
      paneBoardId: null,
      paneAnchor: null,
      menu: null,
      facets: {},
      toast: null,
      interacting: false,
      movingItemIds: [],
      renamingColumnId: null,
      deletingColumnId: null,
      addColumnAfterId: null,
      printing: false,
      expandedItemIds: readExpanded(boardId, viewId),
      subitemColumns: [],
      subitemsBoardId: null,
      subitemsByParent: {},
      subitemsError: null,
      subitemsUnavailableFor: [],
      presence: null,
      removedState: null,
      accessRevoked: false,

      /**
       * **P-081.** The first read cannot know the view's saved query: `effective` is still
       * `DEFAULT_TABLE_VIEW_SETTINGS` when `queryState()` is sampled, and the view's settings
       * arrive *in the answer to that very call*. So a view with a saved filter painted the
       * whole board under a `Filter / 1` badge — and the same went for a saved sort, search,
       * group-by and hidden columns — until the first thing the user touched forced a reload.
       *
       * The answer is re-read **only when it disagrees with what was sent**: a board whose
       * view saves nothing (every board, until someone presses "Save to this view") is still
       * exactly one `boards.read` on open, which is the constraint the card sets.
       */
      async load() {
        try {
          const sent = JSON.stringify(queryState());
          await reload();
          if (JSON.stringify(queryState()) !== sent) await reload();
          void get().refreshFacets();
        } catch (error) {
          set({ status: 'error', error: error instanceof Error ? error.message : 'load failed' });
        }
      },

      /* ── subitems (M3.S1 §6) ───────────────────────────────────────────── */

      async toggleSubitems(itemId) {
        const state = get();
        const open = new Set(state.expandedItemIds.map(String));
        if (open.has(String(itemId))) open.delete(String(itemId));
        else open.add(String(itemId));
        const next = [...open] as ItemId[];
        set({ expandedItemIds: next });
        writeExpanded(boardId, viewId, next);
        // P-308: a refused read collapses the row again from inside `loadSubitems`.
        if (open.has(String(itemId))) await loadSubitems([itemId]);
      },

      async setAllSubitemsExpanded(expanded) {
        const state = get();
        const withSubitems = (state.payload?.groups ?? [])
          .flatMap((group) => [...group.page.items, ...(state.extraRows[group.id] ?? [])])
          .filter((row) => row.subitemIds.length > 0)
          .map((row) => row.id);
        const next = expanded ? withSubitems : [];
        set({ expandedItemIds: next });
        writeExpanded(boardId, viewId, next);
        if (expanded && next.length > 0) await loadSubitems(next);
      },

      async addSubitemInPlace(parentItemId) {
        const before = get().expandedItemIds;
        const opened = !before.some((id) => String(id) === String(parentItemId));
        if (opened) {
          const next = [...before, parentItemId] as ItemId[];
          set({ expandedItemIds: next });
          writeExpanded(boardId, viewId, next);
        }
        const created = await get().addSubitem(parentItemId, 'New subitem');
        /*
         * **P-308.** The create was refused (`addSubitem` has toasted the api's sentence), so
         * the row this gesture opened is put back the way it was — an empty nested block under
         * a board whose subitems cannot be read is the state this round removes.
         */
        if (!created && opened) {
          set({ expandedItemIds: before });
          writeExpanded(boardId, viewId, before);
        }
      },

      async addSubitem(parentItemId, name) {
        const trimmed = name.trim();
        if (trimmed.length === 0) return false;
        try {
          await source.createSubitem({ boardId: boardId as never, parentItemId, name: trimmed });
        } catch (error) {
          /*
           * **P-308.** `+ Add subitem` against an unreachable subitems board answers **the
           * api's own sentence in the toast** — it used to reject into the caller, where the
           * grid had no catch, and the route's error boundary took the board page with it.
           */
          get().showToast({
            message:
              error instanceof Error && error.message.trim() !== ''
                ? error.message
                : 'That subitem could not be created',
            kind: 'error',
          });
          return false;
        }
        await loadSubitems([parentItemId]);
        // The parent's count bubble comes from `subitemIds`, which only the server knows.
        await reload();
        return true;
      },

      /**
       * **P-304 — the subitem row menu's `Move to Item ▸`** (`[uis:05-subitem-row-menu]`).
       * Both parents are repainted: the row leaves one nested table and joins another, and
       * both count bubbles move with it.
       */
      async moveSubitemToParent(subitemId, parentItemId, fromParentItemId) {
        try {
          await source.moveSubitemToParent({ boardId: boardId as never, subitemId, parentItemId });
        } catch (error) {
          get().showToast({
            message: error instanceof Error ? error.message : 'That subitem could not be moved',
            kind: 'error',
          });
          return;
        }
        await loadSubitems([fromParentItemId, parentItemId]);
        await reload();
      },

      /** **P-304 — the subitem row menu's `Convert to item`** (`[uis:05-subitem-row-menu]`). */
      async convertSubitemToItem(subitemId, fromParentItemId) {
        try {
          await source.convertSubitemToItem({ boardId: boardId as never, subitemId });
        } catch (error) {
          get().showToast({
            message: error instanceof Error ? error.message : 'That subitem could not be converted',
            kind: 'error',
          });
          return;
        }
        await loadSubitems([fromParentItemId]);
        await reload();
      },

      async loadMore(groupId) {
        // A page already running for this group (the grid's trigger, or a reload paging back,
        // P-328) is joined, not repeated; whoever started it decides what a failure means.
        const running = inflight.get(groupId);
        if (running) {
          await running;
          return;
        }
        // An `invalid_cursor` means the query changed under us; a reload is the fix (§5.2).
        if ((await fetchPage(groupId)) === 'failed') await reload();
      },

      setEffective(patch) {
        void applySettings(patch);
      },

      resetPersonal() {
        void applySettings(get().saved);
      },

      async saveView() {
        try {
          await source.saveViewSettings({ viewId, settings: get().effective });
          await reload();
          get().showToast({ message: 'Saved to this view', kind: 'info' });
        } catch (error) {
          get().showToast({
            message:
              error instanceof Error && error.message === 'forbidden'
                ? 'Only the view owner or a board owner can save this view'
                : 'The view could not be saved',
            kind: 'error',
          });
        }
      },

      async refreshFacets() {
        try {
          const facets = await source.facets({
            boardId: boardId as never,
            viewId,
            state: queryState(),
          });
          set({ facets });
        } catch {
          set({ facets: {} });
        }
      },

      setSelection(next) {
        set({ selection: next });
      },

      setItemSelection(next) {
        set({ itemSelection: next });
      },

      startEditing(address, initialInput) {
        /*
         * P-326: a subitem cell's column is the **subitems board's**. Its focus is the row's own
         * (`SubitemRow`), so only the editor is opened — the grid selection is built from item
         * rows and has no address for a nested one.
         */
        if (subitemParentOf(address.itemId) !== null) {
          const nested = get().subitemColumns.find((column) => column.id === address.columnId);
          if (!nested || !getColumnUi(nested.type).Editor) return;
          set({ editing: { address, initialInput } });
          return;
        }
        const column = columnOf(address.columnId);
        if (!column) return;
        const ui = getColumnUi(column.type);
        const readOnly = (get().payload?.permissions.readOnlyColumnIds ?? []).includes(address.columnId);
        /*
         * E5: a read-only cell never opens an editor — except the three tier-2 types whose
         * editor is a **panel** rather than a value editor (`file`'s gallery,
         * `time_tracking`'s timer, `mirror`'s reflected list), which monday opens on a
         * read-only column too. They declare it themselves (M2 tier-2 §6).
         */
        if (!ui.Editor || (readOnly && ui.opensWhenReadOnly !== true)) return;
        set({
          selection: { ...focusCell(geometry(), address), editing: true },
          editing: { address, initialInput },
        });
      },

      stopEditing() {
        set((current) => ({ editing: null, selection: { ...current.selection, editing: false } }));
      },

      async commitCell(address, value) {
        set({ editing: null });
        /* P-326: a subitem's cell is written on the subitems board, never the parent board. */
        const subitemParent =
          address.columnId === NAME_COLUMN_ID ? null : subitemParentOf(address.itemId);
        if (subitemParent !== null) {
          await commitSubitemCell(address, value, subitemParent);
          return;
        }
        /*
         * **P-052.** The item name is not a `cell_values` row — `items.name` is its store, and
         * `cells.setMany` skips it — so committing the name cell through `setCellValues`
         * returned `{ written: [], skipped: [] }`, kept the optimistic paint and pushed an undo
         * entry for a write that never happened. Two tabs then disagreed about a name with no
         * error anywhere. The name commits through `items.rename`, which is what
         * `renameItem` has always called and what nothing in the grid used to call.
         */
        if (address.columnId === NAME_COLUMN_ID) {
          await renameFromGrid(address.itemId, value);
          return;
        }
        /*
         * P-263: with a row selection that contains this row, the same value goes to every
         * selected row of this column — one call, so `setCellValues` takes the `cells.setBatch`
         * path it already has for a range (P-110): one `action_record_uuid`, one activity batch
         * row, one realtime envelope per row, one undo entry below.
         */
        const targets = massEditTargets(address);
        const massEdit = targets.length > 1;
        const writes: CellWrite[] = massEdit
          ? targets.map((itemId) => ({ itemId, columnId: address.columnId, value }))
          : [{ itemId: address.itemId, columnId: address.columnId, value }];
        const previous = patchCells(writes);
        /*
         * The optimistic mutation id (M0-F5 §5.3). It rides on `x-mutation-id`, the `events`
         * row echoes it, and the socket handler asks `isOwnMutation` before applying — so
         * this client's own write coming back over the wire is reconciliation, not a repaint
         * (O1). The tracker also owns the rollback for the three failure races (O3, O4, O5).
         */
        const mutationId = newMutationId();
        optimistic.begin(mutationId, previous);
        let result: CellWriteResult;
        try {
          result = await source.setCellValues({ boardId: boardId as never, writes, mutationId });
        } catch (error) {
          // E8 / O3: the grid never keeps a value the server refused, and undoes exactly
          // the fields it painted — `previous` is what `patchCells` reported, nothing more.
          optimistic.fail(mutationId);
          get().showToast({
            message: error instanceof Error ? error.message : 'The cell could not be saved',
            kind: 'error',
          });
          return;
        }
        optimistic.settle(mutationId);
        // P-277: the server's own values go to the grid, and every optimistically painted cell
        // it did not write goes back — a partial refusal used to keep the painted value until
        // the reload, and a whole one was the only case that rolled back.
        applyWriteResult(result, previous);
        if (result.written.length > 0) {
          const id = nextUndoId('edit');
          /*
           * The count is **items**, not writes: a selected row whose value was already the one
           * picked comes back `changed: false` from the no-op gate (B5 §5.1) and is not in
           * `written`, so the toast says what actually changed.
           */
          const items = new Set(result.written.map((write) => String(write.itemId))).size;
          const label = massEdit ? massEditLabel(items) : 'Cell updated';
          set((current) => ({
            undo: pushUndo(current.undo, {
              kind: 'cells',
              id,
              label,
              inverse: result.undo,
              applied: result.written,
              at: Date.now(),
            }),
          }));
          get().showToast({
            message: label,
            actionLabel: 'Undo',
            kind: 'undo',
            undoEntryId: id,
          });
        }
        if (result.skipped.length > 0) {
          get().showToast({
            message:
              result.skipped.length === 1
                ? `1 cell skipped (${result.skipped[0]!.reason})`
                : `${result.skipped.length} cells skipped (${result.skipped[0]!.reason})`,
            kind: 'error',
          });
        }
        await reload();
      },

      async clearSelection() {
        const addresses = rangeAddresses(get().selection);
        if (addresses.length === 0) return;
        /*
         * P-052: the name is not a `cell_values` row, so clearing it through `cells.setMany`
         * wrote nothing and said nothing. An item must have a name — monday offers no way to
         * blank one — so the cell is reported as skipped instead of silently dropped.
         */
        const nameCells = addresses.filter((address) => address.columnId === NAME_COLUMN_ID);
        /*
         * P-130: a column that cannot hold a value is skipped **here**, not refused by the
         * service after a round trip. The reason joins the toast's existing skipped wording.
         */
        const unwritable = addresses.filter(
          (address) => address.columnId !== NAME_COLUMN_ID && !writableColumn(address.columnId),
        );
        const unwritableReasons = unwritable.map(
          (address) => `${columnOf(address.columnId)?.title ?? String(address.columnId)}: read-only`,
        );
        const writes: CellWrite[] = addresses
          .filter((address) => address.columnId !== NAME_COLUMN_ID && writableColumn(address.columnId))
          .map((address) => ({ ...address, value: null }));
        if (writes.length === 0) {
          /*
           * Nothing to send. A selection of nothing but computed cells is monday's *"nothing
           * happens"* — it greys those columns — so it raises no toast either; the name cell
           * keeps the sentence P-052 gave it.
           */
          if (nameCells.length > 0) {
            get().showToast({ message: 'An item name cannot be cleared', kind: 'error' });
          }
          return;
        }
        const previous = patchCells(writes);
        /*
         * P-277: a clear is a write like any other, so it carries a mutation id like any other
         * — the socket handler asks `isOwnMutation` before applying, and this client's own
         * echo of this clear is reconciliation rather than a repaint (§5.3, O1). Without one
         * the grid was at the mercy of whatever that echo carried.
         */
        const mutationId = newMutationId();
        optimistic.begin(mutationId, previous);
        let result: CellWriteResult;
        try {
          result = await source.setCellValues({ boardId: boardId as never, writes, mutationId });
        } catch (error) {
          // `settle`, not `fail`: the rollback and the message below are this action's own,
          // and the tracker's generic "Couldn't save" toast would only flash over them.
          optimistic.settle(mutationId);
          rollbackCells(previous);
          get().showToast({
            message: error instanceof Error ? error.message : 'Those cells could not be cleared',
            kind: 'error',
          });
          return;
        }
        optimistic.settle(mutationId);
        applyWriteResult(result, previous);
        let entryId: string | undefined;
        if (result.undo.length > 0) {
          entryId = nextUndoId('clear');
          const id = entryId;
          set((current) => ({
            undo: pushUndo(current.undo, {
              kind: 'cells',
              id,
              label: clearLabel(result.written.length),
              inverse: result.undo,
              applied: result.written,
              at: Date.now(),
            }),
          }));
        }
        const skipped = result.skipped.map((entry) => entry.reason);
        skipped.push(...unwritableReasons);
        for (const _ of nameCells) skipped.push('the item name cannot be cleared');
        get().showToast({
          message:
            skipped.length === 0
              ? clearLabel(result.written.length)
              : `${clearLabel(result.written.length)} · ${skipped.length} skipped (${skipped[0]})`,
          actionLabel: result.undo.length > 0 ? 'Undo' : undefined,
          kind: 'undo',
          ...(entryId ? { undoEntryId: entryId } : {}),
        });
        await reload();
      },

      async copySelection() {
        const state = get();
        const range = state.selection.range;
        if (!range) return '';
        const context = renderContext();
        const rows = range.itemIds.map((itemId) => {
          const item = itemOf(itemId);
          return range.columnIds.map((columnId) => {
            const column = columnOf(columnId);
            if (!column || !item) return '';
            const ui = getColumnUi(column.type);
            const value = columnId === NAME_COLUMN_ID ? item.name : (item.values[columnId] ?? null);
            // P-126: the context carries the names the people-like types copy by.
            return ui.toClipboard(value, column.settings, context) ?? '';
          });
        });
        const tsv = toTsv(rows);
        // Two flavours, always: Excel and Sheets keep the grid shape from the HTML one (§8.4).
        try {
          if (navigator.clipboard && 'write' in navigator.clipboard && typeof ClipboardItem !== 'undefined') {
            await navigator.clipboard.write([
              new ClipboardItem({
                'text/plain': new Blob([tsv], { type: 'text/plain' }),
                'text/html': new Blob([toHtmlTable(rows)], { type: 'text/html' }),
              }),
            ]);
          } else {
            await navigator.clipboard?.writeText(tsv);
          }
        } catch {
          // A denied clipboard permission must not break the grid; the caller still gets the TSV.
        }
        return tsv;
      },

      async paste(text) {
        const state = get();
        const focus = state.selection.focus;
        if (!focus) return;
        const block = parseTsv(text);
        const geo = geometry();
        const anchorRow = geo.rows.findIndex((row) => row.itemId === focus.itemId);
        const anchorColumn = geo.columnIds.indexOf(focus.columnId);
        if (anchorRow < 0 || anchorColumn < 0) return;

        // C5: clipped to the loaded rows and the visible columns; it creates nothing.
        const targets = clipBlock(
          block,
          geo.rows.map((row) => row.itemId),
          geo.columnIds,
          anchorRow,
          anchorColumn,
        );

        const context = renderContext();

        const writes: CellWrite[] = [];
        /** P-052: name cells of the pasted block, written by `items.rename` below. */
        const renames: CellWrite[] = [];
        const skipped: string[] = [];
        for (const target of targets) {
          const column = columnOf(target.column);
          if (!column) continue;
          // P-130: the board grant **and** the type. A computed column is never pasted into.
          if (!writableColumn(target.column)) {
            skipped.push(`${column.title}: read-only`);
            continue;
          }
          if (target.column === NAME_COLUMN_ID) {
            renames.push({ itemId: target.row, columnId: target.column, value: target.text });
            continue;
          }
          const ui = getColumnUi(column.type);
          try {
            const value =
              target.text.trim() === '' ? null : ui.fromClipboard(target.text, column.settings, context);
            writes.push({ itemId: target.row, columnId: target.column, value });
          } catch (error) {
            skipped.push(`${column.title}: ${error instanceof Error ? error.message : 'not a valid value'}`);
          }
        }

        // E7: chunked, but one undo entry covers the whole paste.
        const undoWrites: CellWrite[] = [];
        const applied: CellWrite[] = [];
        /*
         * P-052: `items.rename` takes one item at a time, and a rename is not undoable through
         * `cells.setMany`'s inverse — so the names are written first and stay out of the undo
         * entry rather than joining it and pretending to be revertible.
         */
        for (const rename of renames) {
          if (await renameFromGrid(rename.itemId, rename.value)) applied.push(rename);
        }
        for (const batch of chunk(writes, PASTE_CHUNK_SIZE)) {
          const previous = patchCells(batch);
          // P-277: one mutation id per chunk — one request, one `x-mutation-id` header — so a
          // paste is filtered out of its own realtime echo exactly as a commit is (§5.3, O1).
          const mutationId = newMutationId();
          optimistic.begin(mutationId, previous);
          try {
            const result = await source.setCellValues({
              boardId: boardId as never,
              writes: batch,
              mutationId,
            });
            optimistic.settle(mutationId);
            /*
             * P-277: the pasted cells paint **here**, from what the server answered, and the
             * ones it refused go back. Until this line the whole paste waited for the reload
             * below, which is why a paste that the server had already accepted showed nothing.
             */
            applyWriteResult(result, previous);
            undoWrites.push(...result.undo);
            applied.push(...result.written);
            for (const entry of result.skipped) skipped.push(entry.reason);
          } catch (error) {
            optimistic.settle(mutationId);
            rollbackCells(previous);
            skipped.push(error instanceof Error ? error.message : 'write refused');
          }
        }
        let entryId: string | undefined;
        if (undoWrites.length > 0) {
          entryId = nextUndoId('paste');
          const id = entryId;
          set((current) => ({
            undo: pushUndo(current.undo, {
              kind: 'cells',
              id,
              label: 'Paste',
              inverse: undoWrites,
              applied,
              at: Date.now(),
            }),
          }));
        }
        get().showToast({
          message: pasteSummary(applied.length, skipped),
          actionLabel: undoWrites.length > 0 ? 'Undo' : undefined,
          kind: 'undo',
          ...(entryId ? { undoEntryId: entryId } : {}),
        });
        await reload();
      },

      /**
       * The one undo (§8.4, P-050). `entryId` comes from the toast that raised it, so its
       * **Undo** inverts *that* action and not whatever landed on the stack afterwards;
       * `Ctrl/Cmd+Z` passes nothing and takes the top of the stack. A delete and an archive
       * invert through `items.restore` / `items.unarchive` — the rows come back in their own
       * group and position, so the grid repaints them where they were.
       */
      async undoLast(entryId) {
        const entry: UndoEntry | null =
          entryId === undefined ? peekUndo(get().undo) : findUndo(get().undo, entryId);
        if (!entry) return;
        set((current) => ({ undo: dropUndo(current.undo, entry.id) }));
        /*
         * P-277: an undo of a cell action is a cell write, so it paints like one — the previous
         * values go on the grid before the request leaves and the server's answer replaces them
         * when it lands. It used to write and then wait for the reload, which is why "Undo" on
         * a paste left the pasted value on screen.
         */
        let undoPainted: readonly CellWrite[] = [];
        let undoMutationId: string | null = null;
        try {
          if (entry.kind === 'cells' && entry.boardId !== undefined) {
            // P-326: a subitem cell's inverse goes back to the subitems board it came from.
            await source.setCellValues({ boardId: entry.boardId as never, writes: entry.inverse, isUndo: true });
            if (entry.parentItemId) await loadSubitems([entry.parentItemId as ItemId]);
          } else if (entry.kind === 'cells') {
            undoPainted = patchCells(entry.inverse);
            undoMutationId = newMutationId();
            optimistic.begin(undoMutationId, undoPainted);
            const result = await source.setCellValues({
              boardId: boardId as never,
              writes: entry.inverse,
              isUndo: true,
              mutationId: undoMutationId,
            });
            optimistic.settle(undoMutationId);
            applyWriteResult(result, undoPainted);
          } else if (entry.kind === 'column') {
            // P-087: the soft-deleted column and every value in it come back in one call.
            await source.restoreColumn({ boardId: boardId as never, columnId: entry.columnId });
          } else {
            const input = {
              // P-304: a subitem's rows go back to the **subitems** board they came from.
              boardId: (entry.boardId ?? boardId) as never,
              itemIds: entry.itemIds,
              actionRecordUuid: entry.actionRecordUuid,
            };
            if (entry.kind === 'restore') await source.restoreItems(input);
            else await source.unarchiveItems(input);
            if (entry.parentItemId) await loadSubitems([entry.parentItemId as ItemId]);
          }
        } catch (error) {
          if (undoMutationId !== null) {
            optimistic.settle(undoMutationId);
            rollbackCells(undoPainted);
          }
          // The entry is put back: a refused undo must stay undoable (E8).
          set((current) => ({ undo: pushUndo(current.undo, entry) }));
          get().showToast({
            message: error instanceof Error ? error.message : 'That action could not be undone',
            kind: 'error',
          });
          return;
        }
        // P-105: what an undo took off is what a redo puts back.
        set((current) => ({ undo: pushRedo(current.undo, entry) }));
        await reload();
        get().showToast({ message: 'Undone', kind: 'info' });
      },

      /**
       * The inverse of the inverse (P-105). A `cells` entry is re-applied from its `applied`
       * half — the values that action wrote — and a `restore` / `unarchive` entry is re-done
       * by removing the rows it put back. Both go through the service as **new** mutations
       * (`is_undo_action: false`), because a redo is not an undo `[ba§2]`.
       */
      async redoLast() {
        const entry = peekRedo(get().undo);
        if (!entry) return;
        set((current) => ({ undo: popRedo(current.undo, entry) }));
        /** P-277: a redo repaints from the answer too — same rule as the undo above. */
        let redoPainted: readonly CellWrite[] = [];
        let redoMutationId: string | null = null;
        try {
          if (entry.kind === 'cells' && entry.boardId !== undefined) {
            await source.setCellValues({ boardId: entry.boardId as never, writes: entry.applied });
            if (entry.parentItemId) await loadSubitems([entry.parentItemId as ItemId]);
          } else if (entry.kind === 'cells') {
            redoPainted = patchCells(entry.applied);
            redoMutationId = newMutationId();
            optimistic.begin(redoMutationId, redoPainted);
            const result = await source.setCellValues({
              boardId: boardId as never,
              writes: entry.applied,
              mutationId: redoMutationId,
            });
            optimistic.settle(redoMutationId);
            applyWriteResult(result, redoPainted);
          } else if (entry.kind === 'column') {
            await source.deleteColumn({ boardId: boardId as never, columnId: entry.columnId });
          } else if (entry.kind === 'restore') {
            await source.deleteItems({ boardId: (entry.boardId ?? boardId) as never, itemIds: entry.itemIds });
            if (entry.parentItemId) await loadSubitems([entry.parentItemId as ItemId]);
          } else {
            await source.archiveItems({ boardId: (entry.boardId ?? boardId) as never, itemIds: entry.itemIds });
            if (entry.parentItemId) await loadSubitems([entry.parentItemId as ItemId]);
          }
        } catch (error) {
          if (redoMutationId !== null) {
            optimistic.settle(redoMutationId);
            rollbackCells(redoPainted);
          }
          // A refused redo stays redoable, and does not pretend to be undoable (E8).
          set((current) => ({ undo: restoreRedo(current.undo, entry) }));
          get().showToast({
            message: error instanceof Error ? error.message : 'That action could not be redone',
            kind: 'error',
          });
          return;
        }
        await reload();
        get().showToast({ message: 'Redone', kind: 'info' });
      },

      async renameItem(itemId, name) {
        await source.renameItem({ boardId: boardId as never, itemId, name });
        await reload();
      },

      async addItem(groupId, name, placement = 'bottom') {
        if (name.trim() === '') return;
        const created = await source.createItem({ boardId: boardId as never, groupId, name, placement });
        await reload();
        await revealRow(groupId, created.id);
      },

      /*
       * **P-291.** Sequential, deliberately: `items.create` appends at the bottom of the group,
       * so the order the lines were pasted in is the order they land in only if each one waits
       * for the previous. Measured on monday (sandbox, 3 lines into `+ Add item`): three items,
       * named line by line, in that group, in that order, **no cells filled and no toast** —
       * so this raises none either, exactly like `addItem` above.
       */
      async addItems(groupId, names, placement = 'bottom') {
        const wanted = names.map((name) => name.trim()).filter((name) => name !== '');
        if (wanted.length === 0) return 0;
        let created = 0;
        let lastId: ItemId | null = null;
        for (const name of wanted) {
          try {
            lastId = (await source.createItem({ boardId: boardId as never, groupId, name, placement })).id;
            created += 1;
          } catch (error) {
            // One refusal must not swallow the rows that did land; the toast says what happened.
            get().showToast({ message: error instanceof Error ? error.message : 'Item refused', kind: 'info' });
            break;
          }
        }
        await reload();
        if (lastId !== null) await revealRow(groupId, lastId);
        return created;
      },

      /**
       * Row delete / archive — the row menu's `Delete`, the item card's, and the multi-select
       * bar's. Both push an undo entry naming the action the service recorded, which is what
       * makes the toast's **Undo** restore exactly those rows (`[B3 D6]`, P-050).
       */
      async deleteItems(itemIds, target) {
        await removeItems(itemIds, 'delete', target);
      },

      async archiveItems(itemIds, target) {
        await removeItems(itemIds, 'archive', target);
      },

      async duplicateItems(itemIds, target) {
        // P-304: a subitem is duplicated **on the subitems board**, beside its sibling.
        await source.duplicateItems({ boardId: (target?.boardId ?? boardId) as never, itemIds });
        if (target) await loadSubitems([target.parentItemId]);
        set({ itemSelection: EMPTY_ITEM_SELECTION });
        await reload();
      },

      /**
       * P-059: `groupId: 'all'` is a value the api's `setCollapsedInput` accepts, so
       * `Collapse all groups` is one call like any other — it used to clear the local set and
       * skip the api, which **expanded** the board the row was meant to collapse.
       */
      async toggleGroupCollapsed(groupId, collapsed) {
        await source.setGroupCollapsed({ boardId: boardId as never, viewId, groupId, collapsed });
        await reload();
      },

      /**
       * The group lifecycle (`[B2§5]`). Each is one service call and a reload: a group change
       * moves rows between pages and recomputes every summary, so there is nothing sensible
       * to patch optimistically the way a single cell can be.
       */
      async addGroup(title, placement = 'top') {
        await source.createGroup({ boardId: boardId as never, ...(title ? { title } : {}), placement });
        await reload();
      },

      async renameGroup(groupId, title) {
        if (title.trim() === '') return;
        await source.renameGroup({ boardId: boardId as never, groupId, title: title.trim() });
        await reload();
      },

      async setGroupColor(groupId, color) {
        await source.setGroupColor({ boardId: boardId as never, groupId, color });
        set({ menu: null });
        await reload();
      },

      async duplicateGroup(groupId, withItems, withUpdates = false) {
        await source.duplicateGroup({ boardId: boardId as never, groupId, withItems, withUpdates });
        await reload();
      },

      async moveGroup(groupId, placement) {
        await source.moveGroup({ boardId: boardId as never, groupId, placement });
        await reload();
      },

      async archiveGroup(groupId) {
        const title = get().payload?.groups.find((group) => group.id === groupId)?.title ?? 'Group';
        await source.archiveGroup({ boardId: boardId as never, groupId });
        await reload();
        get().showToast({ message: `Archived ${title}`, kind: 'info' });
      },

      async deleteGroup(groupId) {
        const title = get().payload?.groups.find((group) => group.id === groupId)?.title ?? 'Group';
        await source.deleteGroup({ boardId: boardId as never, groupId });
        await reload();
        get().showToast({ message: `Deleted ${title}`, kind: 'info' });
      },

      async moveItems(itemIds, groupId) {
        await source.moveItems({ boardId: boardId as never, itemIds, groupId });
        set({ itemSelection: EMPTY_ITEM_SELECTION });
        await reload();
      },

      async reorderItem(itemId, groupId, afterItemId) {
        // P-104: the source sends **one** mutation, and it needs the group the row is leaving
        // to know whether the group is part of what changed.
        const state = get();
        const fromGroupId =
          (state.payload?.groups.find((group) =>
            [...group.page.items, ...(state.extraRows[group.id] ?? [])].some(
              (row) => row.id === itemId,
            ),
          )?.id as GroupId | undefined) ?? null;
        await source.reorderItem({
          boardId: boardId as never,
          itemId,
          groupId,
          afterItemId,
          fromGroupId,
        });
        await reload();
      },

      async reorderItems(itemIds, groupId, afterItemId) {
        if (itemIds.length === 0) return;
        if (itemIds.length === 1) {
          await get().reorderItem(itemIds[0]!, groupId, afterItemId);
          return;
        }
        await source.reorderItems({ boardId: boardId as never, itemIds, groupId, afterItemId });
        await reload();
      },

      /**
       * P-111: the gallery adds to the board the pane was opened for. That is this board for
       * the column-header strip's `+` and the **subitems** board for the nested header's, and
       * the gallery, the registry and `columns.create` were board-agnostic already.
       */
      async addColumn(type, title) {
        const target = get().paneBoardId;
        const subitems = target !== null && String(target) !== String(boardId);
        const visible = subitems ? get().subitemColumns : (get().payload?.columns ?? []);
        const last = visible[visible.length - 1]?.id ?? null;
        /*
         * **P-082.** `Add column to the right` names its anchor; the grid's `+` does not and
         * still appends. The api has taken `afterColumnId` since round 2 — only the client
         * was throwing the anchor away, so every column landed at the far right of the board.
         */
        const anchor = get().addColumnAfterId;
        const after = anchor !== null && visible.some((column) => column.id === anchor) ? anchor : last;
        await source.createColumn({
          boardId: (target ?? boardId) as never,
          type,
          title,
          afterColumnId: after,
        });
        set({ pane: null, paneBoardId: null, paneAnchor: null, addColumnAfterId: null });
        if (subitems) await loadSubitems(get().expandedItemIds);
        await reload();
      },

      async renameColumn(columnId, title) {
        const column = columnOf(columnId);
        if (!column) return;
        await source.updateColumn({
          boardId: boardId as never,
          columnId,
          revision: column.revision,
          title,
        });
        await reload();
      },

      async resizeColumn(columnId, width) {
        const column = columnOf(columnId);
        if (!column) return;
        try {
          await source.updateColumn({
            boardId: boardId as never,
            columnId,
            revision: column.revision,
            width,
          });
          await reload();
        } catch {
          // E8: the loser of a concurrent resize gets a retry toast, never a silent overwrite.
          get().showToast({ message: 'Someone else resized this column — reload to retry', kind: 'error' });
        }
      },

      hideColumn(columnId, hidden) {
        // H4: the name column can never be hidden `[B5§9 L10]`.
        if (columnId === NAME_COLUMN_ID) return;
        const current = get().effective.hiddenColumnIds;
        const next = hidden
          ? [...new Set([...current, columnId])]
          : current.filter((entry) => entry !== columnId);
        void applySettings({ hiddenColumnIds: next });
      },

      pinColumn(columnId, pinned) {
        if (columnId === NAME_COLUMN_ID) return;
        const current = get().effective.pinnedColumnIds;
        const next = pinned
          ? [...new Set([...current, columnId])]
          : current.filter((entry) => entry !== columnId);
        void applySettings({ pinnedColumnIds: next });
      },

      wrapColumn(columnId, wrapped) {
        const current = get().effective.wrappedColumnIds;
        const next = wrapped
          ? [...new Set([...current, columnId])]
          : current.filter((entry) => entry !== columnId);
        void applySettings({ wrappedColumnIds: next });
      },

      async moveColumn(columnId, beforeColumnId) {
        await source.moveColumn({ boardId: boardId as never, columnId, beforeColumnId });
        await reload();
      },

      /**
       * **P-087.** One mis-click used to take a column and every cell in it with no question
       * and no way back. `columns.remove` is a *soft* delete — the row and its `cell_values`
       * survive for the retention window — so the toast's **Undo** is `columns.restore` and
       * brings the values back with the column, not an empty re-creation of it.
       */
      async deleteColumn(columnId) {
        const column = columnOf(columnId);
        await source.deleteColumn({ boardId: boardId as never, columnId });
        const id = nextUndoId('column');
        set((current) => ({
          deletingColumnId: null,
          undo: pushUndo(current.undo, {
            id,
            kind: 'column',
            columnId,
            label: `Deleted ${column?.title ?? 'column'}`,
            at: Date.now(),
          }),
        }));
        await reload();
        get().showToast({
          message: `Deleted ${column?.title ?? 'column'}`,
          actionLabel: 'Undo',
          kind: 'undo',
          undoEntryId: id,
        });
      },

      /** P-084: `columns.duplicate` — settings verbatim, no cells (the service's own Q47). */
      async duplicateColumn(columnId) {
        await source.duplicateColumn({ boardId: boardId as never, columnId });
        await reload();
      },

      async setAggregation(columnId, aggregation) {
        const column = columnOf(columnId);
        if (!column) return;
        await source.updateColumn({
          boardId: boardId as never,
          columnId,
          revision: column.revision,
          aggregation,
        });
        set({ menu: null });
        await reload();
      },

      async updateColumnSettings(columnId, settings) {
        const column = columnOf(columnId);
        if (!column) return false;
        try {
          await source.updateColumn({
            boardId: boardId as never,
            columnId,
            // P-129: `columns.updateSettings` demands it; dropping it made every Save a 400.
            revision: column.revision,
            settings,
          });
          await reload();
          return true;
        } catch (error) {
          /*
           * P-129, second half. The write used to be fire-and-forget behind a `void`, so a
           * refusal was invisible: the dialog closed, the grid kept the old settings and the
           * only trace was a 400 in the network panel. Now the failure is said out loud and
           * the caller is told not to close.
           *
           * `CONFLICT` is a *stale revision* — someone else (or this user's second tab) saved
           * the same column first — so the board is reloaded, which re-seeds the dialog's draft
           * from `column.settings` and gives the retry a revision the server will accept. Every
           * other failure leaves the board alone: reloading would throw the draft away for
           * nothing.
           */
          const conflict = errorCode(error) === 'CONFLICT';
          get().showToast({
            message: conflict
              ? 'Someone else changed this column — your changes were kept, try saving again'
              : "Couldn't save the column settings — try again",
            kind: 'error',
          });
          if (conflict) await reload();
          return false;
        }
      },

      openPane(pane, paneBoardId = null, anchor = null) {
        set((current) => {
          // Re-opening the same pane against a **different** board switches it rather than
          // closing it: the nested `+` and the board's own `+` are two different targets.
          const same = current.pane === pane && (current.paneBoardId ?? null) === paneBoardId;
          // P-082: only `openAddColumnAfter` carries an anchor; every other opener appends.
          return {
            pane: same ? null : pane,
            paneBoardId: same ? null : paneBoardId,
            paneAnchor: same ? null : anchor,
            menu: null,
            addColumnAfterId: null,
          };
        });
      },

      /** P-082: the same gallery, with the anchor the column menu knew and used to discard. */
      openAddColumnAfter(columnId) {
        set({ pane: 'add-column', paneBoardId: null, paneAnchor: null, menu: null, addColumnAfterId: columnId });
      },

      openMenu(menu) {
        set({ menu });
      },

      setMovingItems(itemIds) {
        set({ movingItemIds: itemIds });
      },

      setRenamingColumn(columnId) {
        set({ renamingColumnId: columnId });
      },

      setDeletingColumn(columnId) {
        set({ deletingColumnId: columnId });
      },

      /**
       * **P-090.** `window.print()` on a virtualised grid prints the ~19 rows that happen to
       * be mounted. `printing` takes the virtualiser out of the way for the duration, and the
       * two animation frames give React and the browser one layout pass to paint every row
       * before the (synchronous, blocking) print dialog freezes the page.
       */
      async printBoard() {
        if (typeof window === 'undefined') return;
        set({ printing: true, menu: null, pane: null });
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        try {
          window.print();
        } finally {
          set({ printing: false });
        }
      },

      showToast(toast) {
        toastSequence += 1;
        set({ toast: { ...toast, id: toastSequence } });
      },

      dismissToast() {
        set({ toast: null });
      },

      setInteracting(value) {
        set({ interacting: value });
        // §8.12: an event that would change the row set under a dragging pointer is queued
        // and applied when the pointer is released, not while the row is in the air.
        if (!value) flushQueuedPatches();
      },

      /**
       * One realtime patch (M0-F5 §5.2).
       *
       * `cell.changed` is patched **in place**: it is the event the < 300 ms budget is
       * measured on `[doc08]`, and it is the only one whose whole effect is "this cell now
       * holds that value". Everything structural — an item created or deleted or moved, a
       * group, a column, a view — schedules a **coalesced reload** instead. That is a
       * deliberate trade: hand-patching group pages, cursors and the server-computed group
       * summaries is where a live board goes subtly wrong, while one extra `boards.read`
       * is a single round trip and cannot disagree with the server. A burst of 30 events
       * from an automation chain is still one reload (E10).
       */
      applyRemotePatch(patch) {
        if (get().interacting) {
          queuedPatches.push(patch);
          return;
        }
        switch (patch.kind) {
          case 'ignored':
            return;
          case 'cells':
            patchCells(
              patch.cells.map((cell) => ({
                itemId: cell.itemId as ItemId,
                columnId: cell.columnId as ColumnId,
                value: cell.value,
              })),
            );
            return;
          case 'board.removed':
            set({ removedState: patch.state === 'archived' ? 'archived' : 'deleted' });
            return;
          case 'board.updated':
            // A `permissions`, `kind` or `state` change invalidates what this client may
            // see, so it is a reload rather than a merge (§5.2).
            scheduleReload();
            return;
          default:
            scheduleReload();
        }
      },

      isOwnMutation(mutationId) {
        return optimistic.isPending(mutationId);
      },

      setPresence(roster) {
        set({ presence: roster });
      },

      setAccessRevoked() {
        set({ accessRevoked: true, presence: null });
      },
    };
  });
}

/** Exposed for the realtime layer: drop undo entries a remote change invalidated (§8.4). */
export function invalidateUndo(store: TableStore, changed: readonly CellWrite[]): void {
  const keys = new Set(changed.map((write) => cellKey(write.itemId, write.columnId)));
  store.setState((state) => ({ undo: invalidate(state.undo, keys) }));
}
